import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Item } from "../types/Item";
import auctionService from "../services/auctionService";
import { useAuth } from "../contexts/AuthContext";
import { formatPrice, formatDateTime } from "../utils/formatters";
import { getImageUrl } from "../utils/image";
import CurrencyTicker from "./CurrencyTicker";
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
} from "@mui/material";

interface ItemListProps {
  auctionId?: string;
}

export const ItemList: React.FC<ItemListProps> = ({ auctionId }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const itemsArray = await auctionService.getAuctions("active");
      setItems(itemsArray);
    } catch (err: any) {
      console.error("Error fetching items:", err);
      let errorMessage = "Failed to fetch items. ";
      if (err.message.includes("ERR_CONNECTION_REFUSED")) {
        errorMessage +=
          "Cannot connect to the server. Please make sure the server is running.";
      } else {
        errorMessage += err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies needed

  useEffect(() => {
    fetchItems();
    const interval = setInterval(fetchItems, 120000);
    return () => clearInterval(interval);
  }, [fetchItems]);

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setError(null);
            fetchItems();
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          No active auctions found.
        </Typography>
        {user && (
          <Button
            component={Link}
            to="/create"
            variant="contained"
            color="primary"
          >
            Create New Auction
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 4 }}>
      <CurrencyTicker />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Active Auctions
        </Typography>
        {user && (
          <Button
            component={Link}
            to="/create"
            variant="contained"
            color="primary"
          >
            Create New Auction
          </Button>
        )}
      </Box>
      <Grid container spacing={3}>
        {items.map((item) => {
          console.log("Auction card item:", item);
          return (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card
                component={Link}
                to={`/items/${item.id}`}
                sx={{
                  textDecoration: "none",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={getImageUrl(
                    (item.images && item.images.length > 0 && item.images[0]) ||
                      item.image_url
                  )}
                  alt={item.title}
                  sx={{
                    objectFit: "cover",
                    width: "100%",
                    backgroundColor: "rgba(0,0,0,0.03)",
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/default-auction.png";
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" noWrap gutterBottom>
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.description}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Current Bid:
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        color="success.main"
                        fontWeight="bold"
                      >
                        {formatPrice(
                          item.current_price ?? item.starting_price ?? 0
                        )}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" color="text.secondary">
                        Ends:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(item.end_time)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ItemList;
